import { cn } from '@/lib/utils';
import { TimeOffRequest } from '@/modules/time-off/types/time-off-request';

import { findTimeOffRequestForDay } from '../../../helpers/time-off-request-match.helper';
import { sortTimeOffSummaryItems } from '../../../helpers/time-off-sort.helper';
import { TimeOffSummaryItem } from '../../../types/time-tracking';
import { TimeOffDaySegment } from './timeoff-day-segment';

interface TimeOffDayCellProps {
  /** The day's minimal time-off entries (already weekend-suppressed by the caller). */
  entries: TimeOffSummaryItem[];
  /** Full requests for the week — each segment looks up its match by date + status. */
  requests: TimeOffRequest[];
  date: Date;
  minHeightClass: string;
  onClick: () => void;
}

/**
 * A single day column of the time-off row: stacks one {@link TimeOffDaySegment}
 * per entry (PENDING first), each sized proportionally to its hours. Empty days
 * render a plain bordered cell. Clicking anywhere toggles the row's expansion.
 */
export function TimeOffDayCell({
  entries,
  requests,
  date,
  minHeightClass,
  onClick,
}: TimeOffDayCellProps) {
  if (entries.length === 0) {
    return <div className={cn('border-b border-l', minHeightClass)} />;
  }

  const sorted = sortTimeOffSummaryItems(entries);
  const totalHours = sorted.reduce((sum, entry) => sum + entry.totalHours, 0);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex flex-col overflow-hidden border-b border-l text-left',
        minHeightClass
      )}
    >
      {sorted.map((entry, index) => (
        <TimeOffDaySegment
          key={index}
          entry={entry}
          heightFraction={totalHours > 0 ? entry.totalHours / totalHours : 1}
          match={findTimeOffRequestForDay(requests, date, entry.status)}
          isFirst={index === 0}
        />
      ))}
    </button>
  );
}
